import type { SyntheticEvent } from "react";
import {
    closestCenter,
    KeyboardCode,
    PointerSensor,
    pointerWithin,
    type Activators,
    type Collision,
    type CollisionDetection,
    type KeyboardCodes,
    type PointerSensorOptions,
    type UniqueIdentifier,
} from "@dnd-kit/core";
import {
    currentPlatform,
    getShortcutKeyLabel,
    isPrimaryModifier,
} from "@design-system/Shortcut/formatShortcut";
import type { BoardPlacement } from "./Board.types";
import { getCellKey } from "./boardCells";

export const BOARD_POINTER_ACTIVATION = { distance: 4 };

/** The key held to place a card by hand in a sorted column, for hint copy. */
export const BOARD_PLACE_KEY_LABEL = getShortcutKeyLabel(
    "mod",
    currentPlatform,
);

/** True while the place key (⌘ on Mac, Ctrl elsewhere) is held. */
export const isPlaceModifier = isPrimaryModifier;

export const getCellDroppableId = (placement: BoardPlacement) =>
    `board-cell:${getCellKey(placement)}`;

/** A collapsed column strip; its drops keep the card's lane. */
export const getStripDroppableId = (columnId: string) =>
    `board-strip:${columnId}`;

/**
 * Space picks up so Enter stays free to open the card. Tab cancels instead of
 * dropping, and the sensor prevents its focus move so focus stays on the card.
 */
export const BOARD_KEYBOARD_CODES: KeyboardCodes = {
    start: [KeyboardCode.Space],
    end: [KeyboardCode.Space, KeyboardCode.Enter],
    cancel: [KeyboardCode.Esc, KeyboardCode.Tab],
};

/** Set on a part of a card that counts as a control: a click on it is its own. */
export const BOARD_NO_DRAG_ATTRIBUTE = "data-board-no-drag";

const NO_DRAG_SELECTOR = [
    "a[href]",
    "button",
    "input",
    "select",
    "textarea",
    "[contenteditable='true']",
    "[role='button']",
    "[role='checkbox']",
    "[role='link']",
    "[role='menuitem']",
    "[role='switch']",
    `[${BOARD_NO_DRAG_ATTRIBUTE}]`,
].join(", ");

type EventTargets = Pick<SyntheticEvent, "target" | "currentTarget">;

/** True when the event started on a control inside the card, not the card. */
export const isFromInteractiveDescendant = ({
    target,
    currentTarget,
}: EventTargets) => {
    if (!(target instanceof Element)) {
        return false;
    }
    const control = target.closest(NO_DRAG_SELECTOR);
    return (
        control !== null &&
        control !== currentTarget &&
        currentTarget.contains(control)
    );
};

/**
 * True when the event reached the element through React from a portal it
 * renders, such as a picker's popup, rather than from its own DOM.
 */
export const isFromPortal = ({ target, currentTarget }: EventTargets) =>
    target instanceof Node && !currentTarget.contains(target);

/** True when the event isn't the card's own: from a control in it or a popup it opened. */
export const isFromCardControl = (event: EventTargets) =>
    isFromPortal(event) || isFromInteractiveDescendant(event);

/**
 * Set on a control in a card that Tab reaches after the card, such as an
 * "Add field" chip: focus from the keyboard stays on it instead of moving
 * back to the card. Hide it when it shouldn't be a Tab stop.
 */
export const BOARD_TAB_STOP_ATTRIBUTE = "data-board-tab-stop";

/** True when keyboard focus landed on a card control marked with `BOARD_TAB_STOP_ATTRIBUTE`. */
export const isKeyboardFocusOnTabStop = ({
    target,
}: Pick<SyntheticEvent, "target">) =>
    target instanceof Element &&
    target.closest(`[${BOARD_TAB_STOP_ATTRIBUTE}]`) !== null &&
    target.matches(":focus-visible");

/**
 * Set on a control in a card that still drags the card: a press and move
 * on it drags, a click on it is its own (such as a title that edits on
 * click). See `BoardCardTitleButton`.
 */
export const BOARD_DRAG_THROUGH_ATTRIBUTE = "data-board-drag-through";

const isFromDragThroughControl = (event: EventTargets) =>
    !isFromPortal(event) &&
    event.target instanceof Element &&
    event.target
        .closest(NO_DRAG_SELECTOR)
        ?.hasAttribute(BOARD_DRAG_THROUGH_ATTRIBUTE) === true;

/**
 * Pointer sensor that never starts a drag from a control inside the card
 * (an inline-edit chip) or from a popup the card renders in a portal, except
 * a control marked with `BOARD_DRAG_THROUGH_ATTRIBUTE`.
 */
export class BoardPointerSensor extends PointerSensor {
    static readonly activators = [
        {
            eventName: "onPointerDown",
            handler: (event: SyntheticEvent, { onActivation }) => {
                const { nativeEvent } = event;
                if (
                    !(nativeEvent instanceof PointerEvent) ||
                    !nativeEvent.isPrimary ||
                    nativeEvent.button !== 0 ||
                    (isFromCardControl(event) &&
                        !isFromDragThroughControl(event))
                ) {
                    return false;
                }
                onActivation?.({ event: nativeEvent });
                return true;
            },
        },
    ] satisfies Activators<PointerSensorOptions>;
}

/** Collision data a board hit carries: which half of the card the pointer is in. */
export const isAfterCollision = (collision: Collision | undefined) =>
    collision?.data?.isAfter === true;

interface BoardCollisionDetectionArgs {
    isCellDroppable: (id: UniqueIdentifier) => boolean;
    getCellDroppableIdOfCard: (id: UniqueIdentifier) => string | undefined;
}

/**
 * Pointer-first: the card under the pointer, else the closest card in the
 * column under the pointer, else that empty column. Outside every column
 * there is no hit, so the last slot holds.
 */
export const createBoardCollisionDetection =
    ({
        isCellDroppable,
        getCellDroppableIdOfCard,
    }: BoardCollisionDetectionArgs): CollisionDetection =>
    (args) => {
        const hits = pointerWithin(args);
        const cellHit = hits.find((hit) => isCellDroppable(hit.id));
        const cardHit =
            hits.find((hit) => !isCellDroppable(hit.id)) ??
            (cellHit === undefined
                ? undefined
                : closestCenter({
                      ...args,
                      droppableContainers: args.droppableContainers.filter(
                          (container) =>
                              getCellDroppableIdOfCard(container.id) ===
                              cellHit.id,
                      ),
                  })[0]);
        if (cardHit === undefined) {
            return cellHit === undefined ? [] : [cellHit];
        }
        const rect = args.droppableRects.get(cardHit.id);
        const pointerY = args.pointerCoordinates?.y;
        const isAfter =
            rect !== undefined &&
            pointerY !== undefined &&
            pointerY > rect.top + rect.height / 2;
        return [{ id: cardHit.id, data: { isAfter } }];
    };
